import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useHistory } from "react-router-dom";
import api, { cachedGet } from "../api";
import LottieAnimation from "../LottieAnimation";
import { openChallengeResults } from "./openChallengeResults";

const Banner = styled.div`
  margin: 20px 2vh;
  padding: 20px 24px;
  border-radius: 16px;
  font-family: "Quicksand", sans-serif;
  cursor: pointer;
  background: #8747d4;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  position: relative;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.98);
  }

  @media only screen and (min-width: 601px) {
    margin: 20px 40px;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 1;
  flex-shrink: 0;
  max-width: 55%;
`;

const Title = styled.div`
  font-size: 1.6rem;
  font-weight: 900;
  line-height: 1.2;
`;

const Cta = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const goldShimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const GoldCta = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(90deg, #ffd700, #fff6a8, #ffd700, #ffb300, #ffd700);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${goldShimmer} 3s linear infinite;
`;

const CoversSection = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 100px;
  flex-shrink: 0;
`;

const TrophyWrapper = styled.div`
  width: 85px;
  height: 85px;
  flex-shrink: 0;
  transform: rotate(-12deg);
  margin-right: -8px;
`;

const CoverImage = styled.img`
  width: 70px;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: rotate(${(props) => props.tilt || 0}deg);
  margin-left: ${(props) => props.offset || 0}px;
  object-fit: cover;
  flex-shrink: 0;
`;

function convertToCloudFrontUrl(s3Url) {
  if (!s3Url) return s3Url;
  return s3Url.replace(
    "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
    "https://d10jmd9bhq0s4v.cloudfront.net"
  );
}

function KnowledgeChallengeHomeButton() {
  const history = useHistory();
  const [challenge, setChallenge] = useState(null);
  const [courseImages, setCourseImages] = useState([]);
  const [userHasAttempted, setUserHasAttempted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get("/knowledge-challenge");
      if (!res.data || !res.data.isVisible) return;
      setChallenge(res.data);

      const userEmail = localStorage.getItem("userEmail");
      if (userEmail) {
        const attemptRes = await api.get("/knowledge-challenge/my-attempt", {
          params: { userEmail, challengeId: res.data._id },
        });
        if (attemptRes.data) {
          setUserHasAttempted(true);
        }
      }

      // Home already loaded this; cachedGet reuses it instead of hitting the network again
      const allCourses = await cachedGet("/coursesData", { ttl: 300000 });
      const images = (res.data.courses || [])
        .map((title) => {
          const match = allCourses.find(
            (c) => c.courseTitle.replace(/\s/g, "") === title.replace(/\s/g, "")
          );
          return match ? convertToCloudFrontUrl(match.courseImage) : null;
        })
        .filter(Boolean);
      setCourseImages(images);
    } catch (error) {
      console.error("Error loading challenge data:", error);
    }
  };

  if (!challenge) return null;

  const hasWinner = !!(challenge.winner && challenge.winner.userEmail);

  const ctaText = hasWinner
    ? "We have a winner!"
    : userHasAttempted
    ? "See your result"
    : "Take the challenge";

  const handleClick = () => {
    if (hasWinner) {
      openChallengeResults(history, challenge);
    } else {
      history.push("/knowledge-challenge");
    }
  };

  const tilts = [5, -4, 6, -3];

  return (
    <Banner onClick={handleClick}>
      <TextSection>
        <Title>{challenge.name || "Knowledge Challenge"}</Title>
        {hasWinner ? (
          <GoldCta>🏆 {ctaText} →</GoldCta>
        ) : (
          <Cta>{ctaText} →</Cta>
        )}
      </TextSection>
      <CoversSection>
        <TrophyWrapper>
          <LottieAnimation
            lottieFile={convertToCloudFrontUrl("https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/LottieFiles/spinningTrophy.json")}
            loop={true}
          />
        </TrophyWrapper>
        {courseImages.map((img, idx) => (
          <CoverImage
            key={idx}
            src={img}
            alt="Course cover"
            tilt={tilts[idx % tilts.length]}
            offset={idx === 0 ? 0 : -12}
            loading="lazy"
          />
        ))}
      </CoversSection>
    </Banner>
  );
}

export default KnowledgeChallengeHomeButton;
